import { memo, useRef, useEffect, useState, useCallback } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { amber, green, grey, red } from "@mui/material/colors";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from "../../services/api";
import './style.css'

const Painel = memo(function () {
    const [data, setData] = useState([])
    const [shouldScroll, setShouldScroll] = useState(false)
    const containerRef = useRef(null)
    const contentRef = useRef(null)

    // Processa dados do painel (reutilizado por fetchData e WebSocket)
    const processData = useCallback((responseData) => {
        const empresas = [...new Set(responseData.map(item => item.cod_empresa))]
        return empresas.map(empresa => {
            const warning_alarm_ack = responseData.filter(item => item.cod_empresa === empresa && item.severity == 4 && item.acks == 1)
            const critical_alarm_ack = responseData.filter(item => item.cod_empresa === empresa && item.severity == 5 && item.acks == 1)
            const warning_alarm = responseData.filter(item => item.cod_empresa === empresa && item.severity == 4 && item.acks == 0)
            const critical_alarm = responseData.filter(item => item.cod_empresa === empresa && item.severity == 5 && item.acks == 0)

            return {
                cod_empresa: empresa,
                warning_alarm: warning_alarm[0]?.total || 0,
                critical_alarm: critical_alarm[0]?.total || 0,
                warning_alarm_ack: warning_alarm_ack[0]?.total || 0,
                critical_alarm_ack: critical_alarm_ack[0]?.total || 0
            }
        }).sort((a, b) => a.cod_empresa - b.cod_empresa)
    }, [])

    const fetchData = useCallback(() => {
        api.get('/dashboard/painel-premium').then(response => {
            setData(processData(response.data))
        }).catch(err => console.log(err))
    }, [processData])

    // Carrega inicial
    useEffect(() => {
        fetchData()
    }, [fetchData])

    // Polling a cada 60s (fallback caso WebSocket falhe)
    useEffect(() => {
        const interval = setInterval(() => {
            fetchData()
        }, 60000)
        return () => clearInterval(interval)
    }, [fetchData])

    // Escuta atualizacoes do WebSocket (via evento customizado)
    useEffect(() => {
        const handlePainelUpdate = (event) => {
            if (event.detail && Array.isArray(event.detail)) {
                setData(processData(event.detail))
            }
        }

        window.addEventListener('painel-update', handlePainelUpdate)
        return () => window.removeEventListener('painel-update', handlePainelUpdate)
    }, [processData])

    // Verifica se precisa scroll
    useEffect(() => {
        if (containerRef.current && contentRef.current) {
            const containerWidth = containerRef.current.offsetWidth
            const contentWidth = contentRef.current.scrollWidth
            setShouldScroll(contentWidth > containerWidth)
        }
    }, [data])

    if (data.length === 0) return null;

    // Duplica os dados para scroll infinito suave
    const displayData = shouldScroll ? [...data, ...data] : data

    return (
        <Box
            ref={containerRef}
            className="letreiro-container"
            sx={{
                display: 'flex',
                alignItems: 'center',
                mx: 2,
                maxWidth: '60%',
                minWidth: 200,
            }}
        >
            <Box
                ref={contentRef}
                className={`letreiro ${!shouldScroll ? 'no-scroll' : ''}`}
            >
                {displayData.map((item, index) => (
                    <CompanyCard
                        key={`${item.cod_empresa}-${index}`}
                        cod_empresa={item.cod_empresa}
                        warning_alarm_ack={item.warning_alarm_ack}
                        critical_alarm_ack={item.critical_alarm_ack}
                        warning_alarm={item.warning_alarm}
                        critical_alarm={item.critical_alarm}
                    />
                ))}
            </Box>
        </Box>
    )
})


function CompanyCard({ cod_empresa, warning_alarm, critical_alarm, warning_alarm_ack, critical_alarm_ack }) {
    const hasProblems = critical_alarm > 0 || warning_alarm > 0;
    const allAcked = !hasProblems && (critical_alarm_ack > 0 || warning_alarm_ack > 0);
    const isClean = !hasProblems && !allAcked;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: grey[900],
                borderRadius: 1.5,
                px: 1.5,
                py: 0.8,
                border: '2px solid',
                borderColor: hasProblems ? red[600] : (allAcked ? green[600] : grey[600]),
                flexShrink: 0,
            }}
        >
            <Tooltip title={`Empresa Premium ${cod_empresa}`} arrow>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: grey[100],
                        minWidth: 45,
                        textAlign: 'center',
                    }}
                >
                    {cod_empresa}
                </Typography>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {critical_alarm > 0 && (
                    <Tooltip title="Desastre - Nao vistados" arrow>
                        <Chip
                            icon={<ErrorIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                            label={critical_alarm}
                            size="small"
                            sx={{
                                bgcolor: red[600],
                                color: 'white',
                                fontWeight: 700,
                                fontSize: 14,
                                height: 26,
                                '& .MuiChip-label': { px: 0.6 },
                                '& .MuiChip-icon': { ml: 0.5, mr: -0.3 },
                            }}
                        />
                    </Tooltip>
                )}

                {warning_alarm > 0 && (
                    <Tooltip title="Alta - Nao vistados" arrow>
                        <Chip
                            icon={<WarningAmberIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                            label={warning_alarm}
                            size="small"
                            sx={{
                                bgcolor: amber[700],
                                color: 'white',
                                fontWeight: 700,
                                fontSize: 14,
                                height: 26,
                                '& .MuiChip-label': { px: 0.6 },
                                '& .MuiChip-icon': { ml: 0.5, mr: -0.3 },
                            }}
                        />
                    </Tooltip>
                )}

                {(critical_alarm_ack > 0 || warning_alarm_ack > 0) && (
                    <Tooltip title={`Vistados: ${critical_alarm_ack} desastre, ${warning_alarm_ack} alta`} arrow>
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                            label={critical_alarm_ack + warning_alarm_ack}
                            size="small"
                            sx={{
                                bgcolor: green[600],
                                color: 'white',
                                fontWeight: 700,
                                fontSize: 14,
                                height: 26,
                                '& .MuiChip-label': { px: 0.6 },
                                '& .MuiChip-icon': { ml: 0.5, mr: -0.3 },
                            }}
                        />
                    </Tooltip>
                )}

                {isClean && (
                    <Tooltip title="Sem alarmes" arrow>
                        <CheckCircleIcon sx={{ fontSize: 22, color: green[500] }} />
                    </Tooltip>
                )}
            </Box>
        </Box>
    )
}

export default Painel
